import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { api_urls } from '../api/api-urls';
import { getBrandDetails } from '../services/brand-service';
import { refreshToken } from '../services/login-service';
import { RootState, persistor, store } from '../store';
import { revertAll } from '../store/actions';
import { hideLoader } from '../store/reducers/appSlice';
import { setBrandDetails } from '../store/reducers/brandSlice';
import { resetChatbot } from '../store/reducers/chatbotSlice';
import { updateLoginStatus, updateLoginUserDetails } from '../store/reducers/userSlice';
import { trackLogout } from '../utils/google-analytics';
import { clearStoragePreservingMigrationCookies } from '../utils/migration-cookies';
import { NavigationConfig } from '../utils/navigation-constants';

const INACTIVITY_WARNING_TIME = 4 * 60 * 1000; // 4 minutes of inactivity before showing warning
const INACTIVITY_SESSION_DURATION = 5 * 60 * 1000; // 5 minutes total inactivity before logout (1 min after warning)
const INACTIVITY_BEFORE_LOGOUT = 1 * 60 * 1000;
const COUNTDOWN_INTERVAL = 1000; // 1 second
const ACTIVITY_CHECK_INTERVAL = 1000; // Check for activity every second
const ACTIVITY_RESET_THROTTLE = 1000; // Throttle activity reset to once per second to prevent too frequent resets in dev

export const useSessionTimeout = () => {
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityTimeRef = useRef<number | null>(null);
    const lastResetTimeRef = useRef<number>(0); // Track last reset time for throttling
    const warningShownTimeRef = useRef<number | null>(null); // When the warning modal was first shown

    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const refreshTokenValue = useSelector((state: RootState) => state.user.refreshToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Reset last activity time (called on any user activity)
    // Throttled to prevent too frequent resets (especially in dev environment with React DevTools, etc.)
    const resetLastActivityTime = useCallback(() => {
        const now = Date.now();
        // Throttle: only reset if enough time has passed since last reset
        if (now - lastResetTimeRef.current >= ACTIVITY_RESET_THROTTLE) {
            lastActivityTimeRef.current = now;
            localStorage.setItem('lastActivityTime', now.toString());
            lastResetTimeRef.current = now;
        }
    }, []);

    // Get last activity time from localStorage
    const getLastActivityTime = useCallback(() => {
        const stored = localStorage.getItem('lastActivityTime');
        return stored ? parseInt(stored, 10) : null;
    }, []);

    // Initialize last activity time
    const initializeLastActivityTime = useCallback(() => {
        const stored = getLastActivityTime();
        const now = Date.now();
        lastActivityTimeRef.current = stored || now;
        if (!stored) {
            localStorage.setItem('lastActivityTime', now.toString());
        }
    }, [getLastActivityTime]);

    // Clear last activity time
    const clearLastActivityTime = useCallback(() => {
        localStorage.removeItem('lastActivityTime');
        lastActivityTimeRef.current = null;
    }, []);

    // Calculate time remaining until session expires based on inactivity
    const calculateTimeRemaining = useCallback(() => {
        if (showWarning) {
            if (!warningShownTimeRef.current) return INACTIVITY_BEFORE_LOGOUT;
            const elapsedSinceWarning = Date.now() - warningShownTimeRef.current;
            return Math.max(0, INACTIVITY_BEFORE_LOGOUT - elapsedSinceWarning);
        }
        const lastActivityTime = lastActivityTimeRef.current || getLastActivityTime();
        if (!lastActivityTime) return INACTIVITY_SESSION_DURATION;

        const elapsed = Date.now() - lastActivityTime;
        const remaining = INACTIVITY_SESSION_DURATION - elapsed;
        return Math.max(0, remaining);
    }, [getLastActivityTime, showWarning]);

    // Format time remaining as "X minutes and Y seconds"
    const formatTimeRemaining = useCallback((ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return { minutes, seconds };
    }, []);

    // Log the user out and redirect to login after a failed token refresh
    const handleRefreshFailure = useCallback(async () => {
        clearLastActivityTime();

        // Track before the store is purged below - userDetails won't exist afterwards
        const currentUserId = (store.getState() as RootState).user.userDetails?.Id;
        trackLogout('token_refresh_failed', currentUserId ? String(currentUserId) : undefined);

        // Clear Redux store
        dispatch(revertAll());
        dispatch(updateLoginStatus(false));

        // Clear persisted state
        persistor.purge();

        // Clear localStorage (preserve migration cookies so user lands on login page)
        clearStoragePreservingMigrationCookies();

        navigate(`${NavigationConfig.Login}?source=login`);
        setShowWarning(false);
        dispatch(resetChatbot());

        // Fetch brand details after logout
        try {
            const response = await getBrandDetails(false);
            if (response.statusCode === 200 && response.data) {
                dispatch(setBrandDetails(response.data));
            }
        } catch (error) {
            console.error('Error fetching brand details after logout:', error);
        }
    }, [clearLastActivityTime, dispatch, navigate]);

    // Handle refresh token
    const handleRefreshToken = useCallback(async () => {
        if (!refreshTokenValue || isRefreshing) return;

        setIsRefreshing(true);
        try {
            const response = await refreshToken(refreshTokenValue, true);
            // Backend returns: { statusCode: 200, data: { token: newAccessToken, refreshToken: newRefreshToken } }
            if (response?.statusCode === 200 || response?.data?.token) {
                // Get current user details from store
                const currentState = store.getState() as RootState;
                const currentUserDetails = currentState.user.userDetails;
                // Extract new refresh token from response, fallback to old one if not provided
                const newRefreshToken = response.data?.refreshToken || response?.refreshToken || refreshTokenValue;
                // Update tokens in store (both access token and refresh token)
                dispatch(
                    updateLoginUserDetails({
                        token: response.data?.token || response.data?.accessToken || response.accessToken,
                        refreshToken: newRefreshToken, // Use the new refresh token from response
                        user: currentUserDetails ?? {},
                    }),
                );
                // Clicking "Continue" is itself an explicit activity signal, so reset the inactivity
                // clock here — otherwise lastActivityTime stays exactly as stale as when the warning
                // first appeared, and the very next session check re-triggers the warning immediately.
                if (showWarning) {
                    warningShownTimeRef.current = null;
                    setShowWarning(false);
                    resetLastActivityTime();
                }
            } else {
                // Refresh failed, logout user
                await handleRefreshFailure();
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            await handleRefreshFailure();
        } finally {
            setIsRefreshing(false);
            dispatch(hideLoader());
        }
    }, [refreshTokenValue, isRefreshing, dispatch, handleRefreshFailure, showWarning, resetLastActivityTime]);

    // Close session warning modal (called when user manually dismisses it)
    const handleCloseModal = useCallback(() => {
        warningShownTimeRef.current = null;
        setShowWarning(false);
    }, []);

    // Handle logout
    const handleLogout = useCallback(() => {
        clearLastActivityTime();

        // Track before the store is purged below - userDetails won't exist afterwards
        const currentUserId = (store.getState() as RootState).user.userDetails?.Id;
        trackLogout('session_timeout', currentUserId ? String(currentUserId) : undefined);

        // Clear Redux store
        dispatch(revertAll());
        dispatch(updateLoginStatus(false));

        // Clear persisted state
        persistor.purge();

        // Clear localStorage (preserve migration cookies so user lands on login page)
        clearStoragePreservingMigrationCookies();

        warningShownTimeRef.current = null;
        // Navigate to login (allowed by NoCookieGuard even without migration cookie)
        navigate(`${NavigationConfig.Login}?source=login`);
        setShowWarning(false);
        dispatch(resetChatbot());
    }, [clearLastActivityTime, dispatch, navigate]);

    // Fully logs the user out due to session inactivity: calls the logout API, clears all
    // client-side session state, redirects to login, and re-fetches brand details for the
    // logged-out state. Shared by the periodic session check and the warning countdown timer,
    // which both need to perform this exact sequence when the inactivity window elapses.
    const performInactivityLogout = useCallback(() => {
        clearLastActivityTime();

        // Call logout API
        if (refreshTokenValue) {
            axios({
                method: 'POST',
                url: api_urls.Logout,
                headers: {
                    Authorization: refreshTokenValue,
                },
            });
        }

        // Clear Redux store
        dispatch(revertAll());
        dispatch(updateLoginStatus(false));

        // Clear persisted state
        persistor.purge();

        // Clear localStorage (preserve migration cookies so user lands on login page)
        clearStoragePreservingMigrationCookies();

        warningShownTimeRef.current = null;
        navigate(`${NavigationConfig.Login}?source=login`);
        setShowWarning(false);
        dispatch(resetChatbot());
        // Fetch brand details after logout
        getBrandDetails(false)
            .then(response => {
                if (response.statusCode === 200 && response.data) {
                    dispatch(setBrandDetails(response.data));
                }
            })
            .catch(error => {
                console.error('Error fetching brand details after logout:', error);
            });
    }, [clearLastActivityTime, dispatch, navigate, refreshTokenValue]);

    // Check session timeout based on inactivity
    const checkSessionTimeout = useCallback(() => {
        if (!isLoggedIn) {
            clearLastActivityTime();
            return;
        }

        const lastActivityTime = lastActivityTimeRef.current || getLastActivityTime();
        if (!lastActivityTime) {
            // If no last activity time, initialize it now
            initializeLastActivityTime();
            return;
        }

        const elapsed = Date.now() - lastActivityTime;
        const remaining = INACTIVITY_SESSION_DURATION - elapsed;

        const countdownRemaining =
            showWarning && warningShownTimeRef.current
                ? Math.max(0, INACTIVITY_BEFORE_LOGOUT - (Date.now() - warningShownTimeRef.current))
                : INACTIVITY_BEFORE_LOGOUT;

        if (remaining <= 0 || (showWarning && countdownRemaining <= 0)) {
            // Session expired due to inactivity, logout
            performInactivityLogout();
            return;
        }

        // Show warning after 4 minutes of inactivity
        if (elapsed >= INACTIVITY_WARNING_TIME && !showWarning) {
            warningShownTimeRef.current = Date.now();
            setShowWarning(true);
            setTimeRemaining(INACTIVITY_BEFORE_LOGOUT);
        }

        if (showWarning) {
            setTimeRemaining(countdownRemaining);
        }
    }, [
        isLoggedIn,
        getLastActivityTime,
        initializeLastActivityTime,
        clearLastActivityTime,
        showWarning,
        performInactivityLogout,
    ]);

    // Start countdown timer when warning is shown
    useEffect(() => {
        if (showWarning) {
            countdownRef.current = setInterval(() => {
                const remaining = calculateTimeRemaining();
                setTimeRemaining(remaining);

                if (remaining <= 0) {
                    performInactivityLogout();
                }
            }, COUNTDOWN_INTERVAL);
        } else {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
        }

        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [showWarning, calculateTimeRemaining, performInactivityLogout]);

    // Track user activity events (mouse, keyboard, clicks, scroll, touch)
    useEffect(() => {
        if (!isLoggedIn) return;

        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];

        const handleActivity = () => {
            if (!showWarning) {
                resetLastActivityTime();
            }
        };

        // Add event listeners
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        return () => {
            // Cleanup event listeners
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
        };
    }, [isLoggedIn, resetLastActivityTime, showWarning]);

    // Reset activity time on route changes (navigation)
    useEffect(() => {
        if (isLoggedIn) {
            resetLastActivityTime();
        }
    }, [location.pathname, isLoggedIn, resetLastActivityTime]);

    // Initialize/reset activity time when user logs in
    useEffect(() => {
        if (isLoggedIn) {
            // Reset activity time immediately on login
            resetLastActivityTime();
        } else {
            // Clear activity time on logout
            clearLastActivityTime();
        }
    }, [isLoggedIn, resetLastActivityTime, clearLastActivityTime]);

    // Main session check interval
    useEffect(() => {
        if (isLoggedIn) {
            // Initialize last activity time if not set
            if (!lastActivityTimeRef.current && !getLastActivityTime()) {
                initializeLastActivityTime();
            }

            // Check session every second
            intervalRef.current = setInterval(() => {
                checkSessionTimeout();
            }, ACTIVITY_CHECK_INTERVAL);
        } else {
            clearLastActivityTime();
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isLoggedIn, checkSessionTimeout, getLastActivityTime, initializeLastActivityTime, clearLastActivityTime]);

    return {
        showWarning,
        timeRemaining: formatTimeRemaining(timeRemaining),
        handleRefreshToken,
        handleLogout,
        handleCloseModal,
        isRefreshing,
        resetLastActivityTime, // Expose this so it can be called after API calls, printing, etc.
    };
};
